import React, { useState, useEffect, useContext, createContext } from 'react'
import { User, LoginCredentials, RegisterData } from '@/types'
import { authService } from '@/services/api'
import { storage } from '@/utils'

interface AuthContextType {
  user: User | null
  loading: boolean
  login: (credentials: LoginCredentials) => Promise<void>
  register: (data: RegisterData) => Promise<void>
  logout: () => Promise<void>
  refreshToken: () => Promise<void>
  isAuthenticated: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    initializeAuth()
  }, [])

  const initializeAuth = async () => {
    try {
      console.log('🚀 Initializing authentication...')
      const token = storage.getToken()
      console.log('🔑 Stored token:', token ? 'Found' : 'Not found')
      
      if (token) {
        // Try to validate token and get user profile from API
        try {
          console.log('🔍 Validating token with API...')
          const response = await authService.getProfile()
          console.log('📋 Validation response:', response)
          
          const validationData = response as any
          if (validationData.valid && validationData.user) {
            setUser(validationData.user)
            console.log('✅ User authenticated:', validationData.user.email)
          } else {
            throw new Error('Invalid token')
          }
        } catch (apiError) {
          console.error('❌ API validation failed:', apiError)
          // Clear invalid token
          storage.removeToken()
          storage.removeRefreshToken()
          localStorage.removeItem('user')
          console.log('🧹 Cleared invalid tokens')
        }
      } else {
        console.log('ℹ️ No token found, user needs to login')
      }
    } catch (error) {
      console.error('💥 Failed to initialize auth:', error)
      storage.removeToken()
      storage.removeRefreshToken()
      localStorage.removeItem('user')
    } finally {
      setLoading(false)
      console.log('🏁 Auth initialization complete')
    }
  }

  const login = async (credentials: LoginCredentials) => {
    try {
      setLoading(true)
      console.log('🔐 Starting login process...')
      console.log('📧 Email:', credentials.email)
      console.log('🌐 API URL:', '/api/v1 (via Vite proxy)')
      
      const response = await authService.login(credentials)
      console.log('✅ Login response received:', response)
      console.log('📦 Response data:', response.data)
      
      // The response structure is directly: { access_token, refresh_token, user }
      const authData = response as any
      storage.setToken(authData.access_token)
      console.log('🔑 Token stored:', authData.access_token ? 'Yes' : 'No')
      
      if (authData.refresh_token) {
        storage.setRefreshToken(authData.refresh_token)
        console.log('🔄 Refresh token stored:', authData.refresh_token ? 'Yes' : 'No')
      }
      
      setUser(authData.user)
      console.log('👤 User set:', authData.user)
    } catch (error) {
      console.error('❌ Login error:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const register = async (data: RegisterData) => {
    try {
      setLoading(true)
      const response = await authService.register(data)
      console.log('✅ Register response received:', response)
      console.log('📦 Response data:', response.data)
      
      const authData = response as any
      storage.setToken(authData.access_token)
      if (authData.refresh_token) {
        storage.setRefreshToken(authData.refresh_token)
      }
      
      setUser(authData.user)
    } catch (error) {
      throw error
    } finally {
      setLoading(false)
    }
  }

  const logout = async () => {
    try {
      await authService.logout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      // Clear all authentication data
      storage.removeToken()
      storage.removeRefreshToken()
      localStorage.removeItem('user') // Remove demo user data
      localStorage.removeItem('accessToken') // Remove any other token storage
      localStorage.removeItem('refreshToken')
      setUser(null)
    }
  }

  const refreshToken = async () => {
    try {
      const refreshToken = storage.getRefreshToken()
      if (!refreshToken) {
        throw new Error('No refresh token available')
      }

      const response = await authService.refreshToken(refreshToken)
      console.log('🔄 Refresh token response:', response)
      console.log('📦 Response data:', response.data)
      
      const authData = response as any
      storage.setToken(authData.access_token)
      
      if (authData.refresh_token) {
        storage.setRefreshToken(authData.refresh_token)
      }
      
      // Optionally refresh user data
      const userData = await authService.getProfile()
      const userValidation = userData as any
      if (userValidation.valid && userValidation.user) {
        setUser(userValidation.user)
      }
    } catch (error) {
      console.error('Token refresh failed:', error)
      await logout()
      throw error
    }
  }

  const value: AuthContextType = {
    user,
    loading,
    login,
    register,
    logout,
    refreshToken,
    isAuthenticated: !!user
  }

  return React.createElement(AuthContext.Provider, { value }, children)
}

// Hook for protected routes
export const useRequireAuth = () => {
  const { user, loading } = useAuth()
  
  return {
    user,
    loading,
    isAuthenticated: !!user,
    shouldRedirect: !loading && !user
  }
}

// Hook for guest routes (login/register pages)
export const useGuestOnly = () => {
  const { user, loading } = useAuth()
  
  return {
    user,
    loading,
    shouldRedirect: !loading && !!user
  }
}